import Vimeo from './Vimeo';
import Youtube from './Youtube';

const selectors = {
  videoType: 'data-video',
  playButton: '[data-video-play-button]',
  pauseButton: '[data-video-pause-button]',
  coverImage: '[data-video-cover]',
  modal: '[data-video-modal]',
  modalOpenButton: '[data-video-modal-open]',
  modalCloseButton: '[data-video-modal-close]',
  videoElement: '[data-video-element]',
  modalVideoElement: '[data-video-modal-element]',
  autoplay: 'data-autoplay',
  loop: 'data-loop',
  muted: 'data-muted',
};

export default class Video {
  constructor(el) {
    this.el = el;
    this.platform = el.getAttribute(selectors.videoType)?.trim() || '';
    this.nativeVideo = this.platform === 'native';
    this.playButton = el.querySelector(selectors.playButton);
    this.pauseButton = el.querySelector(selectors.pauseButton);
    this.coverImage = this.el.querySelector(selectors.coverImage);
    this.modal = this.el.querySelector(selectors.modal);
    this.modalOpenButton = this.el.querySelector(selectors.modalOpenButton);
    this.modalCloseButton = this.el.querySelector(selectors.modalCloseButton);
    this.getOptions();
    this.initVideoPlayer();
    this.setupEventListeners();

    // Handle autoplay if enabled
    if (this.options.autoplay) {
      this._autoplay();
      this.setupIntersectionObserver();
    }
  }

  getOptions = () => {
    this.options = {
      autoplay: this.el.getAttribute(selectors.autoplay) === 'true',
      loop: this.el.getAttribute(selectors.loop) === 'true',
      muted: this.el.getAttribute(selectors.muted) === 'true',
    };
  };

  setupIntersectionObserver = () => {
    const options = {
      root: null, // Use viewport as root
      rootMargin: '30%',
      threshold: 0,
    };

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // Only handle pausing when video scrolls out of view
        if (!entry.isIntersecting) {
          this.pause();
        }
      });
    }, options);

    this.observer.observe(this.el);
  };

  initVideoPlayer = () => {
    if (this.nativeVideo) {
      // Handle native HTML5 video
      this.video =
        this.el.tagName === 'VIDEO' ? this.el : this.el.querySelector('video');

      // Initialize the modal video if it exists
      this.modalVideo = this.modal?.querySelector('video');

      if (!this.video && !this.modalVideo) {
        console.error(
          'Video component initialized with data-video="native" but no video element found',
        );
      }
      return;
    }

    // Handle platform videos (YouTube/Vimeo)
    this.videoEl = this.el.querySelector(selectors.videoElement);
    if (!this.videoEl) {
      return;
    }

    const videoData = {
      el: this.videoEl.querySelector('div'),
      videoUrl: this.videoEl.getAttribute('data-video-url'),
      loop: this.options.loop,
    };

    // Create appropriate player based on platform
    this.video =
      {
        youtube: () => new Youtube(videoData),
        vimeo: () => new Vimeo(videoData),
      }[this.platform]?.() || null;

    // Initialize modal video player if exists
    this.modalVideoEl = this.modal?.querySelector(selectors.modalVideoElement);
    if (this.modalVideoEl) {
      const modalVideoData = {
        el: this.modalVideoEl.querySelector('div'),
        videoUrl: this.modalVideoEl.getAttribute('data-video-url'),
        loop: this.options.loop,
      };

      this.modalVideo =
        {
          youtube: () => new Youtube(modalVideoData),
          vimeo: () => new Vimeo(modalVideoData),
        }[this.platform]?.() || null;
    }
  };

  setupEventListeners = () => {
    // Add click event if not native video
    if (!this.nativeVideo) {
      // this.el.addEventListener('click', this._onPlayClick);
    } else if (this.video) {
      this.video.addEventListener('ended', this._onVideoEnded);
    }

    // Add button event listeners
    this.playButton?.addEventListener('click', this._onPlayClick);
    this.pauseButton?.addEventListener('click', this._onPauseClick);

    // Add modal event listeners
    this.modalOpenButton?.addEventListener('click', this._onModalOpenClick);
    this.modalCloseButton?.addEventListener('click', this._onModalCloseClick);

    // Add dialog close on backdrop click
    if (this.modal && typeof this.modal.addEventListener === 'function') {
      this.modal.addEventListener('click', this._onModalBackdropClick);
      // Add event listener for dialog close event
      this.modal.addEventListener('close', this._onModalClosed);
    }
  };

  // Helper method to hide cover image
  _hideCoverImage = () => {
    if (!this.coverImage) {
      return;
    }

    this.coverImage.style.opacity = '0';
    setTimeout(() => {
      this.coverImage.style.display = 'none';
    }, 300);
  };

  // Play native video and handle states
  _playNativeVideo = () => {
    return this.video
      .play()
      .catch(() => console.log('Video playback was prevented by the browser'))
      .finally(() => {
        this.el.classList.add('video-playing');
      });
  };

  _playPlatformVideo = () => {
    this.el.classList.add('video-playing');
  };

  _onPlayClick = () => {
    this._hideCoverImage();

    if (this.nativeVideo) {
      this._playNativeVideo();
    } else if (this.video) {
      this.video.play().then(() => this._playPlatformVideo());
    }
  };

  _onPauseClick = () => {
    if (this.video) {
      if (this.nativeVideo) {
        this.video.pause();
      } else {
        this.video.pause().then(() => {
          this.el.classList.remove('video-playing');
        });
      }
    }
    this.el.classList.remove('video-playing');
  };

  _onVideoEnded = () => {
    this.el.classList.remove('video-playing');
  };

  _autoplay = () => {
    this._hideCoverImage();

    if (this.nativeVideo && this.video) {
      // Ensure video is muted for autoplay
      this.video.muted = true;
      this._playNativeVideo();
    } else if (this.video) {
      this.video.autoplay().then(() => this._playPlatformVideo());
    }
  };

  // Modal methods
  _onModalClosed = () => {
    // Pause video when modal is closed through any method
    this._pauseModalVideo();
  };

  _onModalOpenClick = event => {
    event.preventDefault();
    event.stopPropagation();

    if (this.modal && typeof this.modal.showModal === 'function') {
      this.modal.showModal();

      // Play the modal video
      if (this.nativeVideo && this.modalVideo) {
        this.modalVideo
          .play()
          .catch(err => console.log('Modal video playback was prevented', err));
      } else if (this.modalVideo) {
        this.modalVideo.play();
      }
    }
  };

  _onModalCloseClick = event => {
    event.preventDefault();

    if (this.modal && typeof this.modal.close === 'function') {
      this.modal.close();
      // The pause will be handled by the close event listener
    }
  };

  _onModalBackdropClick = event => {
    // Close dialog when clicking outside the content
    if (event.target === this.modal) {
      this._pauseModalVideo();
      this.modal.close();
    }
  };

  _pauseModalVideo = () => {
    // Pause the modal video when closing
    if (this.nativeVideo && this.modalVideo) {
      this.modalVideo.pause();
      // Reset position
      if (this.modalVideo.currentTime) {
        this.modalVideo.currentTime = 0;
      }
    } else if (this.modalVideo) {
      this.modalVideo.pause();
    }
  };

  // Public methods
  play = () => this._onPlayClick();
  pause = () => this._onPauseClick();

  unload = () => {
    // Remove event listeners
    this.el.removeEventListener('click', this._onPlayClick);
    this.playButton?.removeEventListener('click', this._onPlayClick);
    this.pauseButton?.removeEventListener('click', this._onPauseClick);
    this.modalOpenButton?.removeEventListener('click', this._onModalOpenClick);
    this.modalCloseButton?.removeEventListener(
      'click',
      this._onModalCloseClick,
    );

    if (this.modal && typeof this.modal.removeEventListener === 'function') {
      this.modal.removeEventListener('click', this._onModalBackdropClick);
      this.modal.removeEventListener('close', this._onModalClosed);
    }

    // Clean up intersection observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.nativeVideo && this.video) {
      this.video.removeEventListener('ended', this._onVideoEnded);

      this.video.pause();
      if (this.video.currentTime) {
        this.video.currentTime = 0;
      }
    } else if (this.video) {
      this.video.unload();
    }

    // Clean up modal video
    if (this.nativeVideo && this.modalVideo) {
      this.modalVideo.pause();
      if (this.modalVideo.currentTime) {
        this.modalVideo.currentTime = 0;
      }
    } else if (this.modalVideo) {
      this.modalVideo.unload();
    }
  };

  destroy = () => this.unload();
}
